import { FC, useMemo, useRef } from 'react';
import {
  Center,
  Flex,
  Text,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  IconButton,
} from '@chakra-ui/react';
import { createColumnHelper } from '@tanstack/react-table';
import { DataTable, ServerSort } from 'components/modules/DataTable';
import { HarvestSummaryTabProps } from './HarvestSummary.props';
import { EditHarvestSummaryModal } from 'components/modules/EditHarvestSummaryModal';
import { EditHarvestSummaryFormModal } from 'components/modules/EditHarvestSummaryFormModal';
import RefreshHarvestSummary from './RefreshHarvestSummary';
import HarvestSummaryPdfTemplate from 'components/pdfs/HarvestSummaryPdfTemplate';
import { MdEdit } from 'react-icons/md';
import ArchiveSvg from 'svg/Archive';
import { calculateLost } from 'utils/calculateLost';
import { DeleteReportMenuItem } from './DeleteReportMenuItem';
import { useArchiveReportMutation } from 'api/mutations/archiveReportMutation';
import AddSvg from 'svg/AddSvg';
import { useNavigate } from 'react-router-dom';
import { routesSettings } from 'constants/routes';
import { InventoryReportStatusEnum, Report } from 'types/report';
import { selectors } from 'store/slices/washhub';
import ImpactReportPdf from 'components/pdfs/ImpactReportPdf';
import HarvestSummaryPdfGenerator from './HarvestSummaryPdfGenerator';
import { FaEllipsisV } from 'react-icons/fa';
import { buildHarvestSummaryColumns } from './harvestSummaryColumns';

const actionsCol = createColumnHelper<Report>();

const HarvestSummaryTabContent: FC<HarvestSummaryTabProps> = (props) => {
  const { counts, isLoading, pagination, isArchived, sortHandler } = props;
  const editHarvestSummaryModalRef = useRef<any>(null);
  const editHarvestSummaryFormModalRef = useRef<any>(null);
  const navigate = useNavigate();

  const washHubId = selectors.useSelectedWashHubId();
  const { mutate: archiveReport } = useArchiveReportMutation();

  const handleAddReport = (report: Report) => {
    const url: any =
      routesSettings.PORTAL_IMPACT_REPORT.path +
      `?washHubId=${washHubId}&siteId=${report.site._id}&reportId=${report._id}`;
    navigate(url);
  };

  const columns = useMemo(
    () => [
      ...buildHarvestSummaryColumns(),
      actionsCol.display({
        id: 'actions',
        header: '',
        minSize: 170,
        size: 170,
        meta: { truncate: false },
        cell: ({ row: { original: reportRow } }) => {
          // Cast to `any`, matching the pre-migration `renderRow: (report: any)`
          // signature — reports here may carry an ad hoc `impact` field the
          // base `Report` type doesn't declare.
          const report: any = reportRow;
          const returned = report?.details?.reduce(
            (acc: number, cur: any) => acc + Number(cur.ret || '0'),
            0,
          );
          const lost = report?.details?.reduce(
            (acc: number, cur: any) => acc + calculateLost(cur),
            0,
          );

          // Calculate single_use_items for PDF generation
          const calculatedSingleUseItems = returned + lost;

          // Create enhanced report with calculated single_use_items for Impact PDF
          const enhancedReport = {
            ...report,
            impact: {
              name: report.name,
              startDate: report.startDate,
              endDate: report.endDate,
              month: report.month || 1,
              year: report.year || new Date().getFullYear(),
              ret: returned,
              single_use_items: calculatedSingleUseItems,
              // Default values for other impact metrics (will be filled in actual Impact Report)
              wd: report?.impact?.wd || 0,
              co2m: report?.impact?.co2m || 0,
              wc: report?.impact?.wc || 0,
              er: report?.impact?.er || 0,
              cos: report?.impact?.cos || 0,
            },
          };

          return (
            <Flex
              justifyContent="center"
              alignItems="center"
              gridGap={3}
              onClick={(e) => e.stopPropagation()}>
              <div
                style={{
                  width:
                    report?.status !==
                    InventoryReportStatusEnum.READY_FOR_IMPACT
                      ? '36px'
                      : '0px',
                }}></div>
              <div
                hidden={
                  report.status !== InventoryReportStatusEnum.READY_FOR_IMPACT
                }
                onClick={() => handleAddReport(report)}>
                <AddSvg></AddSvg>
              </div>
              <RefreshHarvestSummary _id={report._id} />

              <HarvestSummaryPdfGenerator
                fileName={`Reuse Report Summary - ${report?.site?.name} - ${report?.name}.pdf`}
                documentTemplate={HarvestSummaryPdfTemplate}
                data={enhancedReport}
                secondFileName={`Impact Report - ${report?.site?.name} - ${report?.name}.pdf`}
                secondDocumentTemplate={ImpactReportPdf}
              />
              <Menu>
                <MenuButton
                  as={IconButton}
                  icon={<FaEllipsisV />}
                  aria-label="Options"
                  variant="ghost"
                />
                <MenuList>
                  {!isArchived && (
                    <>
                      <MenuItem
                        icon={<MdEdit size={16} />}
                        onClick={(e) => {
                          e.stopPropagation();
                          editHarvestSummaryFormModalRef?.current?.onOpen?.(
                            report,
                          );
                        }}>
                        Edit
                      </MenuItem>
                      <MenuItem
                        icon={<ArchiveSvg />}
                        onClick={(e) => {
                          e.stopPropagation();
                          archiveReport(report._id);
                        }}>
                        Archive
                      </MenuItem>
                    </>
                  )}
                  <DeleteReportMenuItem inventoryReportId={report?._id} />
                </MenuList>
              </Menu>
            </Flex>
          );
        },
      }),
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isArchived, washHubId],
  );

  return (
    <Flex
      flex={1}
      direction="column"
      color="support.500"
      overflow="auto"
      h={'full'}>
      <DataTable<Report>
        mt="10px"
        minW="1096px"
        data={counts}
        columns={columns}
        isLoading={isLoading}
        pagination={pagination}
        onSortChange={(s: ServerSort) => sortHandler(s.field)}
        onRowClick={(report) =>
          editHarvestSummaryModalRef?.current?.onOpen?.(report)
        }
        renderEmpty={() => (
          <Center p={8}>
            <Text fontSize="20px">No Harvest Summary available</Text>
          </Center>
        )}
      />
      <EditHarvestSummaryModal ref={editHarvestSummaryModalRef} />
      <EditHarvestSummaryFormModal ref={editHarvestSummaryFormModalRef} />
    </Flex>
  );
};

export default HarvestSummaryTabContent;
